import React, {useEffect, useMemo, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {
  fetchLessonContentAction,
  advancePhase,
  setTutorialViewed,
  setWorkbookReady,
  setWorkbookDone,
  submitWorkbookAnswer,
  submitKCAnswer,
  advanceKCQuestion,
  kcSummaryDone,
  submitMasteryAnswer,
  advanceMasteryQuestion,
  setTutorChatOpen,
  jumpToDevTimelineNode,
  resetViewer,
} from 'store/lessonContentViewer/actions';
import {
  selectViewerLoading,
  selectViewerError,
  selectViewerModel,
  selectCurrentPhase,
  selectActiveTerm,
  selectActiveTermIndex,
  selectActiveQuestionIndex,
  selectWorkbookState,
  selectActiveKCQuestion,
  selectKCAnswers,
  selectKCScore,
  selectActiveMasteryQuestion,
  selectMasteryAnswers,
  selectMasteryScore,
  selectMasteryComplete,
  selectProgressTrackerData,
  selectTutorText,
  selectShowDevTools,
  selectTutorChatOpen,
  selectFrontloadingData,
  selectMetadata,
  selectMasteryQuestions,
} from 'store/lessonContentViewer/selectors';
import LCVFrontloadingStage from './components/LCVFrontloadingStage';
import LCVStaticLessonStage from './components/LCVStaticLessonStage';
import LCVActionSidebar from './components/LCVActionSidebar';
import LCVDevStrip from './components/LCVDevStrip';
import TutorialPhase from './components/TutorialPhase';
import LearningPhase from './components/LearningPhase';
import WorkbookReadyPhase from './components/WorkbookReadyPhase';
import WorkbookOptionsPhase from './components/WorkbookOptionsPhase';
import KnowledgeCheckPhase from './components/KnowledgeCheckPhase';
import KCSummaryPhase from './components/KCSummaryPhase';
import MasteryPhase from './components/MasteryPhase';
import ResultsPhase from './components/ResultsPhase';
import LessonViewerUnityBackground from './LessonViewerUnityBackground';
import './LessonContentViewer.css';

const LessonContentViewer = () => {
  const dispatch = useDispatch();

  const loading = useSelector(selectViewerLoading);
  const error = useSelector(selectViewerError);
  const viewerModel = useSelector(selectViewerModel);
  const phase = useSelector(selectCurrentPhase);
  const activeTerm = useSelector(selectActiveTerm);
  const activeTermIndex = useSelector(selectActiveTermIndex);
  const activeQuestionIndex = useSelector(selectActiveQuestionIndex);
  const workbookState = useSelector(selectWorkbookState);
  const activeKCQuestion = useSelector(selectActiveKCQuestion);
  const kcAnswers = useSelector(selectKCAnswers);
  const kcScore = useSelector(selectKCScore);
  const activeMasteryQuestion = useSelector(selectActiveMasteryQuestion);
  const masteryAnswers = useSelector(selectMasteryAnswers);
  const masteryScore = useSelector(selectMasteryScore);
  const masteryComplete = useSelector(selectMasteryComplete);
  const masteryQuestions = useSelector(selectMasteryQuestions);
  const progressData = useSelector(selectProgressTrackerData);
  const tutorText = useSelector(selectTutorText);
  const showDevTools = useSelector(selectShowDevTools);
  const tutorChatOpen = useSelector(selectTutorChatOpen);
  const frontloadingData = useSelector(selectFrontloadingData);
  const metadata = useSelector(selectMetadata);
  const [visualContent, setVisualContent] = useState(null);
  const [trackerMode, setTrackerMode] = useState('mini');

  const lessonId = useMemo(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('lessonId') || 'G3-MATH-L001-720L';
    }
    return 'G3-MATH-L001-720L';
  }, []);

  useEffect(() => {
    dispatch(fetchLessonContentAction(lessonId));
    return () => dispatch(resetViewer());
  }, [dispatch, lessonId]);

  useEffect(() => {
    setVisualContent(null);
  }, [lessonId]);

  useEffect(() => {
    if (phase === 'frontloading') {
      setTrackerMode('full');
      return undefined;
    }

    if (phase === 'knowledge_check_summary') {
      setTrackerMode('full');
      const collapseTimer = window.setTimeout(() => {
        setTrackerMode('mini');
      }, 5000);
      return () => window.clearTimeout(collapseTimer);
    }

    setTrackerMode('mini');
    return undefined;
  }, [phase]);

  const showShell =
    phase !== 'tutorial' && !loading && !error && viewerModel;
  const useFrontloadingStage =
    showShell && phase === 'frontloading' && progressData && frontloadingData;
  const useStaticStage =
    showShell && phase !== 'frontloading';

  const showMetaverse = [
    'frontloading',
    'learning',
    'workbook_ready',
    'workbook_exercise',
    'workbook_options',
    'knowledge_check',
    'knowledge_check_summary',
  ].includes(phase);

  if (loading) {
    return (
      <div className="lcv-root">
        <div className="lcv-loading">
          <div className="lcv-spinner" />
          <p>Loading lesson...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lcv-root">
        <div className="lcv-error">
          <span className="lcv-error-icon">!</span>
          <h2>Unable to load lesson</h2>
          <p>{error}</p>
          <button
            className="lcv-btn-primary"
            onClick={() => dispatch(fetchLessonContentAction(lessonId))}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!viewerModel) return null;

  return (
    <div className={`lcv-root ${tutorChatOpen ? 'lcv-tutor-active' : ''}`}>
      <LessonViewerUnityBackground />

      {showShell && (
        <LCVActionSidebar
          tutorChatOpen={tutorChatOpen}
          onToggleTutorChat={() => dispatch(setTutorChatOpen(!tutorChatOpen))}
        />
      )}

      {phase === 'tutorial' && (
        <TutorialPhase onComplete={() => dispatch(setTutorialViewed())} />
      )}

      {useFrontloadingStage && (
        <LCVFrontloadingStage
          data={frontloadingData}
          metadata={metadata}
          progressData={progressData}
          phase={phase}
          trackerMode={trackerMode}
          visualContent={visualContent}
          onStart={() => dispatch(advancePhase())}
          paused={tutorChatOpen}
        />
      )}

      {useStaticStage && (
        <LCVStaticLessonStage
          metadata={metadata}
          progressData={progressData}
          phase={phase}
          activeTerm={activeTerm}
          tutorText={tutorText}
          trackerMode={trackerMode}
          visualContent={visualContent}
          middleContent={
            <>
              {phase === 'learning' && activeTerm && (
                <LearningPhase
                  term={activeTerm}
                  termIndex={activeTermIndex}
                  onContinue={() => dispatch(advancePhase())}
                />
              )}

              {(phase === 'workbook_ready' || phase === 'workbook_exercise') &&
                activeTerm && (
                  <WorkbookReadyPhase
                    term={activeTerm}
                    workbookState={workbookState}
                    phase={phase}
                    onReady={() => dispatch(setWorkbookReady())}
                    onDone={() => dispatch(setWorkbookDone())}
                  />
                )}

              {phase === 'workbook_options' && activeTerm && (
                <WorkbookOptionsPhase
                  term={activeTerm}
                  workbookState={workbookState}
                  onSubmit={idx => dispatch(submitWorkbookAnswer(idx))}
                  onContinue={() => dispatch(advancePhase())}
                />
              )}

              {phase === 'knowledge_check' && activeKCQuestion && (
                <KnowledgeCheckPhase
                  question={activeKCQuestion}
                  questionIndex={activeQuestionIndex}
                  totalQuestions={activeTerm?.knowledgeCheck?.questions?.length || 0}
                  kcAnswers={kcAnswers}
                  onSubmit={answer => dispatch(submitKCAnswer(answer))}
                  onNext={() => dispatch(advanceKCQuestion())}
                />
              )}

              {phase === 'knowledge_check_summary' && (
                <KCSummaryPhase
                  score={kcScore}
                  termName={activeTerm?.term}
                  onContinue={() => dispatch(kcSummaryDone())}
                />
              )}

              {phase === 'mastery' && activeMasteryQuestion && (
                <MasteryPhase
                  question={activeMasteryQuestion}
                  questionIndex={activeQuestionIndex}
                  totalQuestions={masteryQuestions.length}
                  masteryAnswers={masteryAnswers}
                  onSubmit={answer => dispatch(submitMasteryAnswer(answer))}
                  onNext={() => dispatch(advanceMasteryQuestion())}
                />
              )}

              {phase === 'results' && (
                <ResultsPhase
                  score={masteryScore}
                  terms={viewerModel.terms}
                  masteryAnswers={masteryAnswers}
                />
              )}
            </>
          }
        />
      )}

      {showDevTools && viewerModel && (
        <LCVDevStrip
          metadata={metadata}
          phase={phase}
          activeTermIndex={activeTermIndex}
          activeQuestionIndex={activeQuestionIndex}
          terms={viewerModel.terms}
          masteryQuestionCount={masteryQuestions.length}
          onJump={node => dispatch(jumpToDevTimelineNode(node))}
        />
      )}
    </div>
  );
};

export default LessonContentViewer;
